import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImageSelectorModalCopy } from "./customModals";

import { InputForm, TextAreaForm } from "../atoms/input";
import { HorizontalLayout, VerticalLayout } from "../components/layout";
import { StandardButton } from "../atoms/buttons";
import { IconPhoto, IconTrash, IconDeviceFloppy, IconX } from "@tabler/icons-react";


function TouchpointPopover({x, y, visible, onClose, readOnly, onUpdate, content, index, isNewTouchpoint, position}) {
  const titleInputRef = useRef(null);
  const [localTitle, setLocalTitle] = useState("");
  const [localDescription, setLocalDescription] = useState("");

  useEffect(() => {
    if (visible && isNewTouchpoint && titleInputRef.current && !readOnly) {
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
          titleInputRef.current.select();
        }
      }, 100);
    }
  }, [visible, isNewTouchpoint, readOnly]);

  useEffect(() => {
    if (visible) {
      const currentTP = content.touchpoints?.[index];
      if (currentTP) {
        setLocalTitle(currentTP.title || "");
        setLocalDescription(currentTP.description || "");
      }
    }
  }, [visible, content, index]);

  if (!visible) return null;

  const positionX = parseFloat(position.x);
  const isRightSide = positionX >= 50;

  let style = {
    top: y,
    position: "absolute",
    zIndex: 1000000,
    backgroundColor: "var(--primary-50)",
    border: "1px solid var(--border-color)",
    padding: "15px",
    borderRadius: "8px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    maxWidth: "20rem",
    minWidth:`calc(15rem - 80px)`,
    transform: "translate(-50%, -50%)",
  };

  if (isRightSide) {
    style["right"] = x;
  } else {
    style["left"] = x;
  }

  const currentTP = content.touchpoints?.[index];
  if (!currentTP) return null;

  const handleSave = () => {
    const newTouchpoints = content.touchpoints.map((tp, i) =>
        i === index ? { ...tp, title: localTitle, description: localDescription } : tp
    );
    onUpdate({ ...content, touchpoints: newTouchpoints });
    onClose();
  };

  const handleRemove = () => {
    const updated = content.touchpoints.filter((tp, i) => i !== index);
    onUpdate({ ...content, touchpoints: updated });
    onClose();
  }

  const handleClose = () => {
    const currentTitle = localTitle.trim();
    const currentDesc = localDescription.trim();
    
    if (!currentTitle && !currentDesc) {
      handleRemove();
    } else {
      onClose();
    }
  }


  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`touchpoint-popover`}
          style={style}
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "top" }}>
            <p className="small-text semi-bold" style={{flexGrow: 1}}>{currentTP.title}</p>
            <button onClick={handleClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem" }}>
                <IconX size={16}/>
            </button>
          </div>
            {readOnly ? (
                <VerticalLayout className="popover-content">
                    <p className="xsmall-text">{currentTP.description}</p>
                </VerticalLayout>
            ) : (
                <VerticalLayout className="popover-editor">
                    <InputForm
                        ref={titleInputRef}
                        title="Titel"
                        value={localTitle}
                        typeInput={setLocalTitle}
                        placeholder="Voer titel in"
                    />
                    <TextAreaForm
                        title="Beschrijving"
                        value={localDescription}
                        typeInput={setLocalDescription}
                        placeholder="Voer beschrijving in"
                    />
                    <HorizontalLayout style={{gap: "0.5rem", marginTop: "0.5rem"}}>
                        <StandardButton 
                            onClick={handleSave} 
                            title={<div className="center-btn"><IconDeviceFloppy width="1rem" height="1rem"/> Opslaan</div>} 
                            backgroundColor="primary-600" 
                            textColor="white" 
                            style={{marginLeft: "0px", flex: 1, textAlign: "center"}}
                        />
                        <StandardButton 
                            onClick={handleRemove} 
                            title={<><IconTrash width="1rem" height="1rem"/></>} 
                            backgroundColor="error-100" 
                            textColor="error-600" 
                            style={{marginLeft: "0px", minWidth: "auto", padding: "0.5rem"}}
                        />
                    </HorizontalLayout>
                </VerticalLayout>
            )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function HoverPreview({ visible, x, y }) {
    if (!visible) return null;

    const style = {
        position: "absolute",
        left: x - 32 / 2,
        top: y - 48 / 2,
        width: `32px`,
        height: `32px`,
        backgroundColor: "rgba(0, 0, 0, 0.2)",
        borderRadius: "50%",
        pointerEvents: "none",
        transition: "opacity 0.1s ease-out",
        boxSizing: "border-box",
        border: "2px dashed rgba(255, 255, 255, 0.7)",
    };

    return (
        <motion.div
          style={style}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15 }}
        >
        </motion.div>
    );
}

export default function InteractiveComp({ readOnly, content, onChange }) {
  let effectiveContent = content;
  let effectiveOnChange = onChange;

  if (!effectiveContent || Object.keys(effectiveContent).length === 0) {
    effectiveContent = { imageUrl: null, touchpoints: [] };
    if (effectiveOnChange && !readOnly) {
      effectiveOnChange(effectiveContent);
    }
  }

  return (
    <>
      {!readOnly && !effectiveContent?.imageUrl && (
        <TouchpointImagePicker imageSelected={effectiveOnChange} />
      )}
      <TouchPointsContainer>
        <TouchpointImage
          readOnly={readOnly}
          content={effectiveContent}
          updateModuleContent={effectiveOnChange}
        />
      </TouchPointsContainer>
    </>
  );
}

function TouchPointsContainer({ children }) {
  return (
    <div className="interactive-comp-container">
        { children }
    </div>
  );
}

function TouchpointImagePicker({ imageSelected }) {
  const [imageModalVisible, setImageModalVisible] = useState(false);
  function selectImage(image) {
    setImageModalVisible(false);
    console.log(image.image.url);
    imageSelected({
      imageUrl: image.image.url,
      touchpoints: [],
    });
  }

  return (
    <>
      <div
        className="box-upload-image-btn"
      >
        <IconPhoto size={64} color="#666"/>
        <h5 style={{margin: "1rem 0px 0.25rem 0px", color: "var(--grey-700)"}}>Geen afbeelding</h5>
        <p style={{margin: "0rem 0px 3rem 0px"}}>Je moet eerst een afbeelding kiezen om een touchpoint te maken.</p>
        <StandardButton backgroundColor="primary-50" textColor="primary-600" onClick={() => setImageModalVisible(true)} title="Afbeelding kiezen"/>
      </div>

      <ImageSelectorModalCopy
        visible={imageModalVisible}
        dismiss={() => setImageModalVisible(false)}
        imageSelected={selectImage}
        type={"a"}
      />
    </>
  );
}

function TouchpointImage({ readOnly, content, updateModuleContent }) {
  const [activeTouchpointIndex, setActiveTouchpointIndex] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: -1, y: -1 });
  const [newTouchpointIndex, setNewTouchpointIndex] = useState(null);

  const handleClick = (e, type) => {
    if (readOnly) return;
    if (type === "r") return;

    if (activeTouchpointIndex !== null) {
      const currentTP = content.touchpoints[activeTouchpointIndex];
      if (currentTP && !currentTP.title.trim() && !currentTP.description.trim()) {
        const updated = content.touchpoints.filter((tp, i) => i !== activeTouchpointIndex);
        updateModuleContent({ ...content, touchpoints: updated });
      }
      setActiveTouchpointIndex(null);
      setNewTouchpointIndex(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const offset = 16;
    const xPercent = ((clickX - offset) / rect.width) * 100;
    const yPercent = ((clickY - offset) / rect.height) * 100;

    addTouchpoint(xPercent, yPercent);
  };

  function addTouchpoint(x, y) {
    const allTouchpoints = [...(content.touchpoints || [])];
    const newTouchpoint = {
      title: "",
      description: "",
      position: {
        x: `${x}%`,
        y: `${y}%`,
      },
    };
    allTouchpoints.push(newTouchpoint);

    updateModuleContent({
      ...content,
      touchpoints: allTouchpoints,
    });

    const indexOfNew = allTouchpoints.length - 1;
    setActiveTouchpointIndex(indexOfNew);
    setNewTouchpointIndex(indexOfNew);
  }

  function touchpointTouched(index, e) {
    e.stopPropagation();
    if (activeTouchpointIndex === index) {
      setActiveTouchpointIndex(null);
      setNewTouchpointIndex(null);
    } else {
      setActiveTouchpointIndex(index);
      setNewTouchpointIndex(null);
    }
  }

  const handleMouseMove = (e) => {
    if (!readOnly) {
        const rect = e.currentTarget.getBoundingClientRect();
        setCursorPos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    }
  }

  const handleMouseLeave = () => {
    setCursorPos({x: -1, y: -1});
  }

  const isHoverVisible = !readOnly && cursorPos.x >= 0;

  return (
    <>
      <div
        style={{ position: "relative", display: "inline-block" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <img
          className="interactive-bg-image"
          onClick={(e) => handleClick(e, "l")}
          onContextMenu={(e) => {
            e.preventDefault();
            handleClick(e, "r");
          }}
          src={content.imageUrl}
          style={{
            maxHeight: "80vh",
            width: "100%",
            height: "auto",
          }}
        />
        <AnimatePresence>
          {(content.imageUrl && content.touchpoints.length == 0) && (
            <motion.div 
              className="touchpoint-hint-pill"
              initial={{ opacity: 0, width: 0, paddingLeft: 0, paddingRight: 0 }}
              animate={{ opacity: 1, width: "auto", paddingLeft: "1rem", paddingRight: "1rem" }}
              exit={{ opacity: 0, width: 0, paddingLeft: 0, paddingRight: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{ overflow: "hidden", whiteSpace: "nowrap" }}
            >
              <b>Tip:</b> Klik ergens in de afbeelding om een touchpoint aan te maken.
            </motion.div>
          )}
        </AnimatePresence>
        
        {isHoverVisible && (
            <HoverPreview visible={true} x={cursorPos.x} y={cursorPos.y} />
        )}

        <AnimatePresence>
          {(content.touchpoints || []).map(function (v, i) {
            const isActive = activeTouchpointIndex === i;
            const isNew = newTouchpointIndex === i;

            return (
              <motion.div
                key={i}
                style={{
                  top: v.position.y,
                  left: v.position.x,
                  cursor: "pointer",
                  zIndex: isActive ? 2:1
                }}
                className={isActive ? "interactive-tp-active" : "interactive-tp"}
                onClick={(e) => touchpointTouched(i, e)}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "backOut" }}
              >
                <TouchpointPopover
                  visible={isActive}
                  onClose={() => {
                    setActiveTouchpointIndex(null);
                    setNewTouchpointIndex(null);
                  }}
                  x={"50%"}
                  y={"0%"}
                  title={v.title}
                  description={v.description}
                  readOnly={readOnly}
                  onUpdate={updateModuleContent}
                  content={content}
                  index={i}
                  isNewTouchpoint={isNew}
                  position={v.position}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </>
  );
}